import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { useDisplayUnit } from '@/hooks/useDisplayUnit';
import { useSendEcash, type SendState } from '@/hooks/useSendEcash';
import { AmountInput } from '@/components/shared/AmountInput';
import { formatXmr, parseUserInput } from '@/lib/monero/units';
import { sumProofs } from '@/lib/cashu/proof';
import { useToast } from '@/hooks/useToast';
import type { WalletMintEntry } from '@/lib/storage/db';
import type { StoredProof } from '@/lib/storage/db';
import { QRCodeCanvas } from '@/components/ui/qrcode';

interface SendEcashDialogProps {
  mint: WalletMintEntry;
  proofs: StoredProof[];
  userPrivkey: string;
}

export function SendEcashDialog({ mint, proofs, userPrivkey }: SendEcashDialogProps) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const { unit, toggle } = useDisplayUnit();
  const { state, prepareToken, confirmSent, reset } = useSendEcash();
  const { toast } = useToast();

  const balance = sumProofs(proofs);

  const handlePrepare = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }
    const piconero = parseUserInput(amount, unit);
    if (piconero <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }
    await prepareToken(proofs, piconero, mint.npub, memo || undefined);
  };

  const handleConfirmSent = async () => {
    if (state.step !== 'ready') return;
    await confirmSent(
      state.token,
      state.amount,
      state.selected,
      mint.npub,
      userPrivkey,
      state.remaining,
    );
  };

  const copyToken = () => {
    if (state.step !== 'ready' && state.step !== 'sent') return;
    navigator.clipboard.writeText(state.token);
    toast({ title: 'Copied!', description: 'Token copied to clipboard.' });
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Send className="h-4 w-4 mr-1" />
          Send
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Ecash</DialogTitle>
          <DialogDescription>
            Create a token to send. You have {formatXmr(balance, 6)} XMR.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {(state.step === 'idle' || state.step === 'error') && (
            <>
              <AmountInput
                value={amount}
                onChange={setAmount}
                unit={unit}
                onUnitToggle={toggle}
                label="Amount to send"
                max={balance}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Memo (optional)</label>
                <input
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="What's this for?"
                />
              </div>
              <Button onClick={handlePrepare} className="w-full">
                Generate Token
              </Button>
              {state.step === 'error' && (
                <p className="text-xs text-destructive">{state.error}</p>
              )}
            </>
          )}

          {state.step === 'preparing' && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Preparing token...</span>
            </div>
          )}

          {(state.step === 'ready' || state.step === 'sent') && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Send this token to the recipient</p>
                <div className="flex justify-center mb-3">
                  <QRCodeCanvas value={state.token} size={200} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Ecash Token</label>
                <div className="flex gap-2">
                  <Textarea
                    value={state.token}
                    readOnly
                    className="text-xs font-mono h-20 resize-none"
                  />
                  <Button size="icon" variant="outline" onClick={copyToken} className="shrink-0">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {state.step === 'ready' && (
                <Button onClick={handleConfirmSent} className="w-full" variant="default">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  I've Sent the Token — Mark as Spent
                </Button>
              )}
              {state.step === 'sent' && (
                <div className="text-center py-2">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                  <p className="text-sm text-green-600">Token marked as spent</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                The recipient must swap this token at the mint to receive fresh ecash.
                Copy the token before closing this dialog.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
